import { AuditableEntity, ID } from '../types/common';
import { DomainError, ValidationError } from '../errors/domainError';
import { ErrorCode } from '../errors/errorCodes';

export interface Variable {
  name: string;
  defaultValue?: string;
  type: 'text' | 'number' | 'selection' | 'date';
  options?: string[]; // For selection type
  required: boolean;
  description?: string;
}

export interface PromptProps {
  id: ID;
  title: string;
  text: string;
  description: string;
  tags: string[];
  folderId: ID;
  variables?: Variable[];
  createdAt: number;
  updatedAt: number;
  lastChanged?: number; // Add lastChanged property
  createdBy?: ID;
  updatedBy?: ID;
}

export class Prompt implements AuditableEntity {
  public readonly id: ID;
  public readonly title: string;
  public readonly text: string;
  public readonly description: string;
  public readonly tags: readonly string[];
  public readonly folderId: ID;
  public readonly variables: readonly Variable[];
  public readonly createdAt: number;
  public readonly updatedAt: number;
  public readonly lastChanged?: number; // Add lastChanged property
  public readonly createdBy?: ID;
  public readonly updatedBy?: ID;

  protected constructor(props: PromptProps) {
    this.id = props.id;
    this.title = props.title;
    this.text = props.text;
    this.description = props.description;
    this.tags = Object.freeze([...props.tags]);
    this.folderId = props.folderId;
    this.variables = Object.freeze(props.variables || []);
    this.createdAt = props.createdAt;
    this.updatedAt = props.updatedAt;
    this.lastChanged = props.lastChanged;
    this.createdBy = props.createdBy;
    this.updatedBy = props.updatedBy;

    Object.freeze(this);
  }

  public static create(
    props: Omit<PromptProps, 'id' | 'createdAt' | 'updatedAt' | 'variables'>
  ): Prompt {
    const now = Date.now();
    return new Prompt({
      ...props,
      id: crypto.randomUUID(),
      createdAt: now,
      updatedAt: now,
      lastChanged: now, // Set lastChanged to creation timestamp
      variables: Prompt.extractVariables(props.text),
    });
  }

  public static fromProps(props: PromptProps): Prompt {
    return new Prompt(props);
  }

  private static validateTitle(title: string): void {
    if (!title.trim()) {
      throw new ValidationError('Title cannot be empty');
    }
    if (title.length > 100) {
      throw new ValidationError('Title cannot be longer than 100 characters');
    }
  }

  private static validateText(text: string): void {
    if (!text.trim()) {
      throw new ValidationError('Prompt text cannot be empty');
    }
    if (text.length > 10000) {
      throw new ValidationError('Prompt text cannot be longer than 10000 characters');
    }
  }

  private static validateTags(tags: string[]): void {
    if (tags.some(tag => !tag.trim())) {
      throw new ValidationError('Tags cannot be empty');
    }
    if (tags.some(tag => tag.length > 50)) {
      throw new ValidationError('Tags cannot be longer than 50 characters');
    }
    if (new Set(tags).size !== tags.length) {
      throw new ValidationError('Duplicate tags are not allowed');
    }
  }

  private static extractVariables(text: string): Variable[] {
    const matches = text.match(/\{\{([^}]+)\}\}/g) || [];
    const variables = new Map<string, Variable>();

    matches.forEach(match => {
      const name = match.slice(2, -2).trim();
      if (!variables.has(name)) {
        variables.set(name, {
          name,
          type: 'text',
          required: true,
        });
      }
    });

    return Array.from(variables.values());
  }

  public update(changes: Partial<Omit<PromptProps, 'id' | 'createdAt' | 'updatedAt'>>): Prompt {
    const now = Date.now();
    const updatedProps = {
      ...this.toObject(),
      ...changes,
      updatedAt: now,
      lastChanged: now, // Update lastChanged to current timestamp
    };

    // Keep variables in sync with the text. When the text changes and the
    // caller didn't explicitly supply variables, re-extract them — otherwise
    // the prompt kept its old {{placeholders}} after an edit (#112). An explicit
    // `variables` in changes still wins.
    if (changes.text !== undefined && changes.variables === undefined) {
      updatedProps.variables = Prompt.extractVariables(updatedProps.text);
    }

    // Re-validate the entire prompt
    Prompt.validateTitle(updatedProps.title);
    Prompt.validateText(updatedProps.text);
    Prompt.validateTags(updatedProps.tags);

    return new Prompt(updatedProps);
  }

  public addTag(tag: string): Prompt {
    if (this.tags.includes(tag)) {
      throw new DomainError(ErrorCode.VALIDATION_ERROR, 'Tag already exists');
    }
    return this.update({ tags: [...this.tags, tag] });
  }

  public removeTag(tag: string): Prompt {
    if (!this.tags.includes(tag)) {
      throw new DomainError(ErrorCode.VALIDATION_ERROR, 'Tag does not exist');
    }
    return this.update({ tags: this.tags.filter(t => t !== tag) });
  }

  public moveToFolder(folderId: ID): Prompt {
    if (this.folderId === folderId) {
      throw new DomainError(ErrorCode.VALIDATION_ERROR, 'Prompt is already in this folder');
    }
    return this.update({ folderId });
  }

  public toObject(): PromptProps {
    return {
      id: this.id,
      title: this.title,
      text: this.text,
      description: this.description,
      tags: [...this.tags],
      folderId: this.folderId,
      variables: [...this.variables],
      createdAt: this.createdAt,
      updatedAt: this.updatedAt,
      lastChanged: this.lastChanged,
      createdBy: this.createdBy,
      updatedBy: this.updatedBy,
    };
  }

  public equals(other: Prompt): boolean {
    return this.id === other.id;
  }
}
